import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth/auth.context'
import { LanguageSwitcher } from '../../components/language-switcher'
import {
  marketingPlanPriceLabel,
  parseMarketingPlan,
  postAuthPath,
  withPlanQuery,
} from '../billing/marketing-plan'
import { useMarketingPlan } from '../billing/use-marketing-plan'
import { apiErrorBanner, isEmailAlreadyRegistered, parseApiError } from '../../lib/api-error'
import { companyService } from '../../services'
import { ErrorMessage } from './components/error-message'

const inputClass =
  'w-full rounded-2xl border bg-white/90 px-4 py-3 text-[#12304a] shadow-[0_4px_16px_rgba(56,189,248,0.08)] outline-none transition-all duration-200 placeholder:text-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:opacity-50'

const inputOkClass =
  'border-sky-100 hover:border-sky-300 focus:border-sky-300 focus:ring-sky-200/60'

const inputBadClass =
  'border-error-500 hover:border-error-500 focus:border-error-500 focus:ring-error-100'

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null
  return (
    <p id={`${id}-error`} className='mt-2 text-sm font-medium text-error-700'>
      {message}
    </p>
  )
}

const labelClass = 'mb-2 block text-sm font-semibold text-[#12304a]'

const primaryButtonClass =
  'w-full rounded-2xl bg-gradient-to-r from-sky-400 to-blue-400 px-6 py-3.5 font-semibold text-white shadow-[0_14px_32px_rgba(56,189,248,0.24)] transition-all duration-200 hover:-translate-y-0.5 hover:brightness-[1.02] hover:shadow-[0_20px_42px_rgba(56,189,248,0.28)] focus:outline-none focus:ring-4 focus:ring-sky-200/70 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0'

const pageBackgroundStyle = {
  background:
    'radial-gradient(circle at top left, rgba(125, 211, 252, 0.22), transparent 30%), radial-gradient(circle at 85% 12%, rgba(96, 165, 250, 0.16), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f4faff 38%, #eaf6ff 100%)',
}

const gridBackgroundStyle = {
  backgroundImage:
    'linear-gradient(rgba(186, 230, 253, 0.32) 1px, transparent 1px), linear-gradient(90deg, rgba(186, 230, 253, 0.32) 1px, transparent 1px)',
  backgroundSize: '44px 44px',
  maskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 88%)',
  WebkitMaskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 88%)',
}

const animationStyleHtml = {
  __html: `
    @keyframes slide-up {
      from {
        opacity: 0;
        transform: translateY(24px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-slide-up {
      animation: slide-up 0.55s ease-out;
    }
  `,
}

const getValue = (ref: { current: HTMLInputElement | HTMLTextAreaElement | null }) => {
  return ref.current?.value.trim() ?? ''
}

export default function CompanySignupPage() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isLoading: authLoading } = useAuth()
  const plan = useMarketingPlan()
  const queryPlan = parseMarketingPlan(searchParams.get('plan'))

  const companyNameRef = useRef<HTMLInputElement>(null)
  const companyDescriptionRef = useRef<HTMLTextAreaElement>(null)
  const contactPersonNameRef = useRef<HTMLInputElement>(null)
  const contactPersonEmailRef = useRef<HTMLInputElement>(null)
  const contactPersonPhoneRef = useRef<HTMLInputElement>(null)
  const adminFirstNameRef = useRef<HTMLInputElement>(null)
  const adminLastNameRef = useRef<HTMLInputElement>(null)
  const adminEmailRef = useRef<HTMLInputElement>(null)
  const adminPasswordRef = useRef<HTMLInputElement>(null)
  const adminPasswordConfirmRef = useRef<HTMLInputElement>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false)
  const [success, setSuccess] = useState<string>()

  const controlClass = (field: string, extra?: string) =>
    [inputClass, fieldErrors[field] ? inputBadClass : inputOkClass, extra].filter(Boolean).join(' ')

  const invalidProps = (field: string) =>
    fieldErrors[field]
      ? { 'aria-invalid': true as const, 'aria-describedby': `${field}-error` }
      : {}

  useEffect(() => {
    if (authLoading || !user || !queryPlan) return
    if (user.role === 'EMPLOYEE') return
    nav(postAuthPath(queryPlan), { replace: true })
  }, [authLoading, user, queryPlan, nav])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setError(undefined)
    setFieldErrors({})
    setEmailAlreadyRegistered(false)
    setSuccess(undefined)

    const companyName = getValue(companyNameRef)
    const companyDescription = getValue(companyDescriptionRef)
    const contactPersonName = getValue(contactPersonNameRef)
    const contactPersonEmail = getValue(contactPersonEmailRef)
    const contactPersonPhone = getValue(contactPersonPhoneRef)
    const adminFirstName = getValue(adminFirstNameRef)
    const adminLastName = getValue(adminLastNameRef)
    const adminEmail = getValue(adminEmailRef)

    const adminPassword = adminPasswordRef.current?.value ?? ''
    const adminPasswordConfirm = adminPasswordConfirmRef.current?.value ?? ''

    if (adminPassword !== adminPasswordConfirm) {
      setFieldErrors({})
      setEmailAlreadyRegistered(false)
      setError(t('auth.passwordsMismatch'))
      return
    }

    setLoading(true)

    try {
      await companyService.signupCompany({
        companyName,
        companyDescription: companyDescription || undefined,
        contactPersonName: contactPersonName || undefined,
        contactPersonEmail,
        contactPersonPhone: contactPersonPhone || undefined,
        adminFirstName,
        adminLastName,
        adminEmail,
        adminPassword,
      })

      setSuccess(
        plan ? t('auth.signupSuccessWithPlan', { licenses: plan }) : t('auth.signupSuccess')
      )

      setTimeout(() => nav(withPlanQuery('/login', plan)), 1200)
    } catch (e: unknown) {
      const parsed = parseApiError(e)
      setFieldErrors(parsed.fieldErrors)
      setEmailAlreadyRegistered(isEmailAlreadyRegistered(parsed))
      setError(
        apiErrorBanner(parsed, {
          network: t('errors.network'),
          unknown: t('errors.unknownServer'),
        })
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className='relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10 text-[#12304a]'
      style={pageBackgroundStyle}
    >
      <LanguageSwitcher variant='auth' className='fixed absolute top-4 right-4 z-20' />

      <div className='pointer-events-none fixed inset-0' style={gridBackgroundStyle} />

      <div className='pointer-events-none absolute -right-24 top-20 h-[460px] w-[min(48vw,580px)] rounded-full bg-gradient-to-br from-sky-300/25 to-blue-300/20 blur-3xl' />
      <div className='pointer-events-none absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-sky-200/30 blur-3xl' />

      <div className='relative z-10 w-full max-w-2xl animate-slide-up'>
        <div className='rounded-[2rem] border border-sky-200/80 bg-white/90 p-8 shadow-[0_16px_50px_rgba(56,189,248,0.10)] backdrop-blur-2xl transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_20px_56px_rgba(14,165,233,0.14)]'>
          <div className='mb-8 text-center'>
            <h1 className='mb-2 text-3xl font-bold tracking-tight text-slate-950'>
              {t('auth.companySignupTitle')}
            </h1>

            <p className='text-sm text-slate-600'>{t('auth.companySignupSubtitle')}</p>
          </div>

          {plan && (
            <div
              data-testid='marketing-plan-banner'
              className='mb-6 rounded-2xl border border-sky-200 bg-sky-50/90 p-4 text-sm leading-6 text-sky-900'
            >
              {t('auth.selectedPlanBanner', {
                licenses: plan,
                price: marketingPlanPriceLabel(plan),
              })}
            </div>
          )}

          <form onSubmit={handleSubmit} className='space-y-6'>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='md:col-span-2'>
                <label htmlFor='companyName' className={labelClass}>
                  {t('auth.companyName')}
                </label>

                <input
                  id='companyName'
                  ref={companyNameRef}
                  required
                  className={controlClass('companyName')}
                  placeholder={t('auth.companyNamePlaceholder')}
                  disabled={loading}
                  {...invalidProps('companyName')}
                />
                <FieldError id='companyName' message={fieldErrors.companyName} />
              </div>

              <div className='md:col-span-2'>
                <label htmlFor='companyDescription' className={labelClass}>
                  {t('auth.companyDescription')}
                </label>

                <textarea
                  id='companyDescription'
                  ref={companyDescriptionRef}
                  rows={3}
                  className={controlClass('companyDescription', 'resize-none')}
                  placeholder={t('auth.companyDescriptionPlaceholder')}
                  disabled={loading}
                  {...invalidProps('companyDescription')}
                />
                <FieldError id='companyDescription' message={fieldErrors.companyDescription} />
              </div>

              <div>
                <label htmlFor='contactPersonName' className={labelClass}>
                  {t('auth.contactPersonName')}
                </label>

                <input
                  id='contactPersonName'
                  ref={contactPersonNameRef}
                  className={controlClass('contactPersonName')}
                  placeholder='Max Mustermann'
                  disabled={loading}
                  {...invalidProps('contactPersonName')}
                />
                <FieldError id='contactPersonName' message={fieldErrors.contactPersonName} />
              </div>

              <div>
                <label htmlFor='contactPersonEmail' className={labelClass}>
                  {t('auth.contactPersonEmail')}
                </label>

                <input
                  id='contactPersonEmail'
                  ref={contactPersonEmailRef}
                  type='email'
                  required
                  className={controlClass('contactPersonEmail')}
                  placeholder='kontakt@unternehmen.de'
                  disabled={loading}
                  {...invalidProps('contactPersonEmail')}
                />
                <FieldError id='contactPersonEmail' message={fieldErrors.contactPersonEmail} />
              </div>

              <div className='md:col-span-2'>
                <label htmlFor='contactPersonPhone' className={labelClass}>
                  {t('auth.contactPersonPhone')}
                </label>

                <input
                  id='contactPersonPhone'
                  ref={contactPersonPhoneRef}
                  type='tel'
                  className={controlClass('contactPersonPhone')}
                  placeholder='+49 123 456789'
                  disabled={loading}
                  {...invalidProps('contactPersonPhone')}
                />
                <FieldError id='contactPersonPhone' message={fieldErrors.contactPersonPhone} />
              </div>
            </div>

            <div className='border-t border-sky-100 pt-6'>
              <h2 className='mb-4 text-lg font-bold text-slate-950'>{t('auth.administrator')}</h2>

              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div>
                  <label htmlFor='adminFirstName' className={labelClass}>
                    {t('common.firstName')}
                  </label>

                  <input
                    id='adminFirstName'
                    ref={adminFirstNameRef}
                    required
                    className={controlClass('adminFirstName')}
                    placeholder='Max'
                    disabled={loading}
                    {...invalidProps('adminFirstName')}
                  />
                  <FieldError id='adminFirstName' message={fieldErrors.adminFirstName} />
                </div>

                <div>
                  <label htmlFor='adminLastName' className={labelClass}>
                    {t('common.lastName')}
                  </label>

                  <input
                    id='adminLastName'
                    ref={adminLastNameRef}
                    required
                    className={controlClass('adminLastName')}
                    placeholder='Mustermann'
                    disabled={loading}
                    {...invalidProps('adminLastName')}
                  />
                  <FieldError id='adminLastName' message={fieldErrors.adminLastName} />
                </div>

                <div className='md:col-span-2'>
                  <label htmlFor='adminEmail' className={labelClass}>
                    {t('auth.adminEmail')}
                  </label>

                  <input
                    id='adminEmail'
                    ref={adminEmailRef}
                    type='email'
                    required
                    className={controlClass('adminEmail')}
                    placeholder='admin@unternehmen.de'
                    disabled={loading}
                    {...invalidProps('adminEmail')}
                  />
                  <FieldError id='adminEmail' message={fieldErrors.adminEmail} />
                </div>

                <div>
                  <label htmlFor='adminPassword' className={labelClass}>
                    {t('auth.password')}
                  </label>

                  <input
                    id='adminPassword'
                    ref={adminPasswordRef}
                    type='password'
                    required
                    minLength={8}
                    className={controlClass('adminPassword')}
                    placeholder={t('auth.passwordPlaceholder')}
                    disabled={loading}
                    {...invalidProps('adminPassword')}
                  />
                  <FieldError id='adminPassword' message={fieldErrors.adminPassword} />
                </div>

                <div>
                  <label htmlFor='adminPasswordConfirm' className={labelClass}>
                    {t('auth.confirmPassword')}
                  </label>

                  <input
                    id='adminPasswordConfirm'
                    ref={adminPasswordConfirmRef}
                    type='password'
                    required
                    minLength={8}
                    className={controlClass('adminPasswordConfirm')}
                    placeholder={t('auth.passwordRepeatPlaceholder')}
                    disabled={loading}
                    {...invalidProps('adminPasswordConfirm')}
                  />
                  <FieldError
                    id='adminPasswordConfirm'
                    message={fieldErrors.adminPasswordConfirm}
                  />
                </div>
              </div>
            </div>

            {error && (
              <ErrorMessage
                message={error}
                action={
                  emailAlreadyRegistered ? (
                    <p>
                      {t('auth.alreadyHaveAccount')}{' '}
                      <Link
                        to={withPlanQuery('/login', plan)}
                        className='inline-flex rounded-xl bg-white px-3 py-1.5 font-semibold text-error-700 shadow-sm ring-1 ring-error-200 hover:bg-error-50'
                      >
                        {t('auth.toLogin')}
                      </Link>
                    </p>
                  ) : undefined
                }
              />
            )}

            {success && (
              <div className='rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4 animate-slide-up'>
                <div className='flex items-start'>
                  <svg
                    className='mr-2 mt-0.5 h-5 w-5 shrink-0 text-emerald-500'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>

                  <span className='text-sm text-emerald-700'>{success}</span>
                </div>
              </div>
            )}

            <button type='submit' disabled={loading} className={primaryButtonClass}>
              {loading ? (
                <div className='flex items-center justify-center'>
                  <svg
                    className='-ml-1 mr-3 h-5 w-5 animate-spin text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />

                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  {t('common.processing')}
                </div>
              ) : (
                t('auth.registerCompany')
              )}
            </button>

            <div className='text-center'>
              <p className='text-sm text-slate-600'>
                {t('auth.alreadyHaveAccount')}{' '}
                <Link
                  to={withPlanQuery('/login', plan)}
                  className='font-semibold text-sky-700 transition-colors duration-200 hover:text-sky-600'
                >
                  {t('auth.toLogin')}
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      <style dangerouslySetInnerHTML={animationStyleHtml} />
    </div>
  )
}
